import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Plus, Trash2, Pencil, Grid3X3, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = { stand_number: '', zone: 'A', size: 'standard', price: 0 };

const STATUS_MAP = {
  available: { label: 'Disponible', color: 'bg-green-100 text-green-700' },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  reserved: { label: 'Réservé', color: 'bg-blue-100 text-blue-700' },
};

export default function ManageStands() {
  const { token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchItems = async () => {
    try {
      const res = await axios.get(`${API}/stands`, { headers });
      setItems(res.data);
    } catch (e) { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async () => {
    if (!form.stand_number) { toast.error('Numéro de stand requis'); return; }
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/stands/${editId}`, form, { headers });
        toast.success('Stand mis à jour');
      } else {
        await axios.post(`${API}/stands`, form, { headers });
        toast.success('Stand créé');
      }
      setDialogOpen(false);
      setForm(emptyForm);
      setEditId(null);
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
    finally { setSaving(false); }
  };

  const handleEdit = (item) => {
    setForm({ stand_number: item.stand_number, zone: item.zone, size: item.size, price: item.price });
    setEditId(item.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer ce stand ?')) return;
    try {
      await axios.delete(`${API}/stands/${id}`, { headers });
      toast.success('Stand supprimé');
      fetchItems();
    } catch (e) { toast.error('Erreur'); }
  };

  const openAdd = () => { setForm(emptyForm); setEditId(null); setDialogOpen(true); };

  const filtered = filter === 'all' ? items : items.filter(i => i.status === filter);

  const formatPrice = (p) => new Intl.NumberFormat('fr-FR').format(p) + ' FCFA';

  return (
    <div data-testid="manage-stands">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1E3A5F]">Stands</h1>
          <p className="text-sm text-[#6B7C93]">{items.length} stand(s) au total</p>
        </div>
        <div className="flex gap-3">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous</SelectItem>
              <SelectItem value="available">Disponibles</SelectItem>
              <SelectItem value="pending">En attente</SelectItem>
              <SelectItem value="reserved">Réservés</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={openAdd} className="bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="add-stand-btn">
            <Plus className="w-4 h-4 mr-2" />Ajouter
          </Button>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        {Object.entries(STATUS_MAP).map(([key, { label, color }]) => {
          const count = items.filter(i => i.status === key).length;
          return (
            <Card key={key} className="border-0 shadow-sm">
              <CardContent className="p-4 flex items-center justify-between">
                <span className="text-sm text-[#6B7C93]">{label}</span>
                <span className={`text-sm font-bold px-3 py-1 rounded-full ${color}`}>{count}</span>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>N°</TableHead>
                <TableHead>Zone</TableHead>
                <TableHead>Taille</TableHead>
                <TableHead>Prix</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead>Exposant</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((item) => {
                const st = STATUS_MAP[item.status] || STATUS_MAP.available;
                return (
                  <TableRow key={item.id}>
                    <TableCell className="font-mono font-bold">{item.stand_number}</TableCell>
                    <TableCell>Zone {item.zone}</TableCell>
                    <TableCell className="capitalize">{item.size}</TableCell>
                    <TableCell>{formatPrice(item.price)}</TableCell>
                    <TableCell><span className={`text-xs px-2 py-1 rounded-full ${st.color}`}>{st.label}</span></TableCell>
                    <TableCell className="text-sm">{item.exhibitor?.company_name || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(item)}><Pencil className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 text-[#6B7C93]">
              <Grid3X3 className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Aucun stand</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle>{editId ? 'Modifier' : 'Ajouter'} un stand</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div><Label>Numéro *</Label><Input value={form.stand_number} onChange={(e) => setForm({ ...form, stand_number: e.target.value })} placeholder="A01" data-testid="stand-number-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Zone</Label>
                <Select value={form.zone} onValueChange={(v) => setForm({ ...form, zone: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="A">Zone A (Premium)</SelectItem>
                    <SelectItem value="B">Zone B (Standard)</SelectItem>
                    <SelectItem value="C">Zone C (Compact)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Taille</Label>
                <Select value={form.size} onValueChange={(v) => setForm({ ...form, size: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="standard">Standard</SelectItem>
                    <SelectItem value="compact">Compact</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Prix (FCFA)</Label><Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseInt(e.target.value) || 0 })} /></div>
            <Button onClick={handleSave} disabled={saving} className="w-full bg-[#1E3A5F] hover:bg-[#152A45]" data-testid="stand-submit-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editId ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
